import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react'
import { useForm } from 'react-hook-form'
import {
  PiPaperclip,
  PiWarning,
  PiEnvelopeSimple,
  PiGear,
  PiCaretDown,
  PiCheck,
} from 'react-icons/pi'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from 'components/UI/BaseDropdown/dropdown-base'
import {
  ds2026DropdownContentClass,
  ds2026DropdownItemClass,
} from 'components/FbUI/ds2026DropdownMenu'
import { useModalDepth } from 'components/UI/Modal/ModalPrimitive'
import { getDropdownZIndex } from 'styles/z-index'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import useApiService from 'services/api'
import CreatableSelect from 'components/FormUtils/CreatableSelect'
import {
  CcBccToggles,
  ComposeMode,
  emailRecipientStyles,
  filterSelf,
  formatEmailRecipientOption,
  looksLikeEmail,
  RecipientRow,
  ReplyMode,
} from 'components/FormUtils/EmailRecipientSelect'
import { InlineComposeShell, InlineReplyHeader } from 'components/Email'
import { TextInput } from 'components/FormUtilsV2/TextInput/text-input'
import { StyledEditorRHF } from 'components/Editor/EditorRHF'
import {
  AttachmentList,
  Attachment,
  isNewAttachment,
} from 'components/Attachments/AttachmentList'
import {
  ALLOWED_EMAIL_ATTACHMENT_TYPES,
  MAX_EMAIL_ATTACHMENT_SIZE,
  EMAIL_ATTACHMENT_WARNING_THRESHOLD,
  getAcceptString,
} from 'constants/fileTypes'
import { useFileAttachments } from 'hooks/useFileAttachments'
import { useRecipientOptions } from 'hooks/useRecipientOptions'
import { cn } from 'components/UI/cn'
import { useAuth } from 'context/authentication/useAuth'
import {
  EmailContact,
  EmailMessage,
  EmailThread,
  EmailSignature,
} from 'models/emailIntegration'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import FbButton from 'components/FbUI/FbButton'
import { Ds2026ButtonProvider } from 'components/FbUI/Ds2026ButtonContext'
import DOMPurify from 'dompurify'
import { COMPANY_BADGE_CLASS, DealBadge } from './DealBadge'

export type { ComposeMode }

/** Payload passed from ComposePanel to parent for optimistic send. */
export interface SendPayload {
  optimisticMessage: EmailMessage
  formData: FormData
  fromId: number
}

/** Create a chip from free-text input, rejecting anything that doesn't look
 *  like an email (second line of defense — menu-level `isValidNewOption` is
 *  the first). */
const createEmailOption = async (inputValue: string) => {
  if (!looksLikeEmail(inputValue)) return null
  return { label: inputValue, value: inputValue }
}

interface ComposePanelProps {
  mode: ComposeMode
  thread?: EmailThread
  replyToMessage?: EmailMessage
  integrationEmail?: string
  onSend: (payload: SendPayload) => void
  onDiscard: () => void
  onModeChange?: (mode: ComposeMode) => void
  dealIdentifier?: string | null
  dealName?: string | null
  companyName?: string
  /** When true, renders without its own container border/padding (parent provides the chrome). */
  standalone?: boolean
  /** When true, hides the toolbar Send/Cancel buttons — the parent is
   *  expected to trigger send via the imperative ref handle (e.g. a
   *  global Apply button in a wrapping modal). Also hides the deal/
   *  company badges since the parent renders its own entity context. */
  embedded?: boolean
  /** Override the default ``To`` list (otherwise derived from ``mode``/
   *  ``replyToMessage``). Use when the parent has the recipient ahead of
   *  time and the original message isn't available — e.g. embedded
   *  compose triggered from a recommendation card. */
  initialTo?: string[]
  /** Override the default subject. */
  initialSubject?: string
  /** Override the default body (HTML). */
  initialBody?: string
  /** Extra contacts to inject into the recipient autocomplete options so
   *  they render as proper chips with name + email. Required when the
   *  parent supplies ``initialTo`` for an email that isn't already
   *  reachable via ``replyToMessage`` or the contacts API. */
  initialContacts?: EmailContact[]
  /** Optional node rendered to the left of the subject input as a
   *  prefix. Used by callers that want to surface contextual chrome
   *  (e.g. a "New thread" badge) attached to the subject row without
   *  modifying the subject's value itself. */
  subjectPrefix?: React.ReactNode
  /** Optional node rendered at the right end of the subject row, after the
   *  subject input. Use for trailing context (e.g. a "See thread" link)
   *  that should sit opposite the label rather than crowding the value. */
  subjectSuffix?: React.ReactNode
  /** When true, collapses the signature footer (picker row + HTML
   *  preview) into a single inline chip in the toolbar so it doesn't
   *  eat vertical space. Used by callers that put the compose panel
   *  inside a height-constrained container (e.g. the rec sider's
   *  apply panel) where every pixel of body-editor real estate
   *  matters. The picker itself still works — preview is dropped. */
  compactSignature?: boolean
}

/** Imperative handle exposed to parents that need to trigger send from
 *  outside the panel (used in embedded mode where the toolbar Send is hidden).
 *
 *  ``submit`` returns the underlying ``handleSubmit`` Promise so callers can
 *  ``await`` validation. react-hook-form runs the resolver inside a Promise
 *  chain even with sync zod schemas, so ``submit()`` is NOT synchronous —
 *  parents that read the resulting payload right after must ``await``. */
export interface ComposePanelHandle {
  submit: () => Promise<void>
}

const composeSchema = z.object({
  to: z.array(z.string().email()).min(1, 'At least one recipient is required'),
  cc: z.array(z.string().email()).optional(),
  bcc: z.array(z.string().email()).optional(),
  from_id: z
    .number({ message: 'Please select a from email' })
    .min(1, 'Please select a from email'),
  subject: z.string().min(1, 'Subject is required'),
  body: z.string().min(1, 'Message body is required'),
})

type ComposeFormValues = z.infer<typeof composeSchema>

/** Accessor for the shared `filterSelf` against EmailContact shape. */
const getContactEmail = (c: EmailContact) => c.email

/** Build the default "To" recipients based on compose mode. */
function getDefaultTo(
  mode: ComposeMode,
  replyToMessage?: EmailMessage,
  integrationEmail?: string
): string[] {
  if (mode === 'compose' || mode === 'forward') return []

  const sender = replyToMessage?.sender || []
  const senderEmails = filterSelf(
    sender,
    integrationEmail,
    getContactEmail
  ).map((c) => c.email)

  // When replying to your own outbound message, address the original recipients
  // instead of yourself (filterSelf would leave an empty list).
  const fallbackTo = filterSelf(
    replyToMessage?.to || [],
    integrationEmail,
    getContactEmail
  ).map((c) => c.email)

  if (mode === 'reply') {
    return senderEmails.length > 0 ? senderEmails : fallbackTo
  }

  // reply_all: original sender + original To recipients (minus self)
  // Original Cc recipients go to getDefaultCc
  return [...new Set([...senderEmails, ...fallbackTo])]
}

/** Build the default "Cc" recipients based on compose mode. */
function getDefaultCc(
  mode: ComposeMode,
  replyToMessage?: EmailMessage,
  integrationEmail?: string
): string[] {
  if (mode !== 'reply_all') return []
  return filterSelf(
    replyToMessage?.cc || [],
    integrationEmail,
    getContactEmail
  ).map((c) => c.email)
}

/** Build default subject — no stacking of Re:/Fwd: per RFC 5322. */
function getDefaultSubject(
  mode: ComposeMode,
  thread?: EmailThread,
  replyToMessage?: EmailMessage
): string {
  const originalSubject = replyToMessage?.subject || thread?.subject || ''

  if (mode === 'compose') return ''

  if (mode === 'forward') {
    // Don't stack Fwd:, but preserve any existing Re: prefix
    if (/^Fwd:/i.test(originalSubject)) return originalSubject
    return `Fwd: ${originalSubject}`
  }

  // reply / reply_all — don't stack Re:
  if (/^Re:/i.test(originalSubject)) return originalSubject
  return `Re: ${originalSubject}`
}

/** Format an email contact list as "Name <email>, ..." for display in quoted headers. */
function formatContactList(contacts: EmailContact[] | null): string {
  if (!contacts?.length) return ''
  return contacts
    .map((c) =>
      c.name ? `${c.name} &lt;${c.email}&gt;` : `&lt;${c.email}&gt;`
    )
    .join(', ')
}

/** Build default body (quoted original for forward, Gmail-style header). */
function getDefaultBody(
  mode: ComposeMode,
  replyToMessage?: EmailMessage
): string {
  if (mode !== 'forward' || !replyToMessage?.body) return ''

  const from = formatContactList(replyToMessage.sender)
  const date = replyToMessage.date
    ? new Date(replyToMessage.date).toLocaleString()
    : ''
  const to = formatContactList(replyToMessage.to)
  const cc = formatContactList(replyToMessage.cc)

  const headerLines = [
    `From: ${from || 'Unknown'}`,
    `Date: ${date}`,
    `Subject: ${replyToMessage.subject || ''}`,
    `To: ${to}`,
  ]
  if (cc) headerLines.push(`Cc: ${cc}`)

  return `<br/><br/><div style="border-left:3px solid #ccc;padding-left:12px;color:#555">
<p>---------- Forwarded message ---------</p>
<p>${headerLines.join('<br/>')}</p>
<br/>
${replyToMessage.body}
</div>`
}

/** Thin alias so we can spread shared props into To/Cc/Bcc recipient fields. */
const RecipientSelect = CreatableSelect

/** Avatar + email row for the From integration picker. */
function formatIntegrationOption(
  option: { label: string; value: number },
  { context }: { context: 'menu' | 'value' }
) {
  if (context === 'value') return option.label
  const initial = option.label[0]?.toUpperCase() || '?'
  return (
    <div className="flex items-center gap-3 w-full">
      <div className="size-8 rounded-full bg-neutral-100 flex items-center justify-center flex-shrink-0">
        <span className="text-xs font-medium text-neutral-600">{initial}</span>
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm text-neutral-800 truncate font-medium">
          {option.label}
        </div>
      </div>
    </div>
  )
}

/** Bottom-of-compose section: signature picker, preview, and config link.
 *
 *  When at least one signature exists, the footer shows the picker on
 *  the left, a "Configure" link on the right, and the selected
 *  signature's HTML preview underneath. When the user has zero
 *  signatures, the picker/preview is replaced with a yellow warning
 *  prompting them to set one up.
 *
 *  Sanitized server-side at create-time, but we run the HTML through
 *  DOMPurify here too as defence-in-depth before any dangerouslySetInnerHTML.
 */
function SignatureFooter({
  signatures,
  selectedSignature,
  selectedSignatureId,
  onChange,
  hasNoSignatures,
}: {
  signatures: EmailSignature[]
  selectedSignature: EmailSignature | null
  selectedSignatureId: number | null
  onChange: (id: number | null) => void
  hasNoSignatures: boolean
}) {
  // Memo on stable primitives, not the parent's freshly-built object
  // reference. selectedSignature is recomputed (signatures.find(...))
  // on every parent render; depending on it would invalidate the memo
  // every tick and re-run sanitize unnecessarily. id+html together are
  // a strict subset of selectedSignature's identity for sanitize, so
  // the lint warning here is a false positive.
  const sanitizedHtml = useMemo(
    () => (selectedSignature ? DOMPurify.sanitize(selectedSignature.html) : ''),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedSignature?.id, selectedSignature?.html]
  )
  const modalDepth = useModalDepth()
  const zIndex = useMemo(() => getDropdownZIndex(modalDepth), [modalDepth])
  const selectedLabel = selectedSignature
    ? `${selectedSignature.name}${selectedSignature.is_default ? ' (default)' : ''}`
    : 'No signature'
  // Sits in the editor box between the body and the toolbar so it
  // reads as part of the composed email. Slight off-white tint is
  // enough delineation; no top border (the body's editor padding
  // gives natural separation already).
  return (
    <div className="flex-shrink-0 border-y border-neutral-200 bg-brand-sage-50 text-sm">
      {hasNoSignatures ? (
        <div className="flex items-center gap-2 bg-yellow-50 border-b border-yellow-200 text-yellow-800 px-3 py-2">
          <PiWarning size={14} className="flex-shrink-0" />
          <span className="flex-1">No Email Signature Configured</span>
          <Link
            to="/settings/email-integration/signatures"
            className="font-medium underline hover:text-yellow-900 whitespace-nowrap"
          >
            Configure
          </Link>
        </div>
      ) : (
        <>
          <div className="flex items-center gap-2 px-3 py-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  aria-label="Email signature"
                  className="inline-flex items-center gap-1 h-7 max-w-[180px] rounded-md border border-neutral-300 bg-white px-2 text-sm font-medium text-neutral-800 transition-colors hover:bg-neutral-100 data-[state=open]:bg-neutral-100"
                >
                  <span className="truncate">{selectedLabel}</span>
                  <PiCaretDown className="size-3.5 flex-shrink-0 text-neutral-500" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="start"
                style={{ minWidth: 180, zIndex }}
                className={ds2026DropdownContentClass}
              >
                <DropdownMenuItem
                  className={cn(ds2026DropdownItemClass, '!text-sm')}
                  onSelect={() => onChange(null)}
                >
                  <span className="flex-1">No signature</span>
                  {selectedSignatureId == null && (
                    <PiCheck className="size-4 flex-shrink-0 text-brand-active" />
                  )}
                </DropdownMenuItem>
                {signatures.map((s) => (
                  <DropdownMenuItem
                    key={s.id}
                    className={cn(ds2026DropdownItemClass, '!text-sm')}
                    onSelect={() => onChange(s.id)}
                  >
                    <span className="flex-1 truncate">
                      {s.name}
                      {s.is_default ? ' (default)' : ''}
                    </span>
                    {selectedSignatureId === s.id && (
                      <PiCheck className="size-4 flex-shrink-0 text-brand-active" />
                    )}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <Link
              to="/settings/email-integration/signatures"
              className="ml-auto inline-flex items-center gap-1 text-brand-active font-medium hover:text-brand-pressed whitespace-nowrap"
            >
              <PiGear className="size-4" />
              Configure
            </Link>
          </div>
          {selectedSignature && (
            <div
              className="px-3 pb-3 pt-1 text-neutral-500 [&_a]:text-brand-active [&_a]:underline"
              dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
            />
          )}
        </>
      )}
    </div>
  )
}

/** Whether the compose mode is inline within a thread (not standalone). */
function isInlineMode(mode: ComposeMode): boolean {
  return mode === 'reply' || mode === 'reply_all' || mode === 'forward'
}

/** Compose panel for reply, reply all, forward, and new compose. */
export const ComposePanel = forwardRef<ComposePanelHandle, ComposePanelProps>(
  function ComposePanel(
    {
      mode,
      thread,
      replyToMessage,
      integrationEmail,
      onSend,
      onDiscard,
      onModeChange,
      dealIdentifier,
      dealName,
      companyName,
      standalone,
      embedded,
      initialTo,
      initialSubject,
      initialBody,
      initialContacts,
      subjectPrefix,
      subjectSuffix,
      compactSignature,
    },
    ref
  ) {
    const api = useApiService()
    const { user } = useAuth()
    const [attachments, setAttachments] = useState<Attachment[]>([])
    const [showCc, setShowCc] = useState(() => {
      if (mode === 'reply_all') {
        const cc = getDefaultCc(mode, replyToMessage, integrationEmail)
        return cc.length > 0
      }
      return false
    })
    const [showBcc, setShowBcc] = useState(false)
    const inline = isInlineMode(mode)

    const {
      fileInputRef,
      openFileSelect,
      handleFileSelect,
      createRemoveHandler,
    } = useFileAttachments({
      allowedTypes: ALLOWED_EMAIL_ATTACHMENT_TYPES,
      maxFileSize: MAX_EMAIL_ATTACHMENT_SIZE,
      maxTotalSize: MAX_EMAIL_ATTACHMENT_SIZE,
      onAdd: (newAttachments) =>
        setAttachments((prev) => [...prev, ...newAttachments]),
    })

    // Only sendable accounts -- an expired one auto-selected fails on send.
    const { data: emailIntegrationOptions, isLoading: integrationsLoading } =
      api.emailIntegration.useGetSendableEmailIntegrationOptions()
    const hasNoSendableIntegrations =
      !integrationsLoading && (emailIntegrationOptions?.length ?? 0) === 0

    // ---------------------------------------------------------------
    // Signatures
    // ---------------------------------------------------------------
    // Fetch the current user's signatures, default-promoted first by
    // the backend ordering. We surface them in a small toolbar dropdown
    // and append the selected one to the body on send.
    const { data: signaturesPage } = useQuery({
      queryKey: ['email-signatures'],
      queryFn: ({ signal }) =>
        api.emailIntegration.getEmailSignatureList(signal),
      staleTime: 5 * 60 * 1000,
    })
    const signatures: EmailSignature[] = signaturesPage?.results ?? []
    const defaultSignature =
      signatures.find((s) => s.is_default) ?? signatures[0] ?? null
    // Derive the selection instead of syncing it via useEffect: track
    // whether the user has explicitly picked something, and otherwise
    // fall back to the default. This way picking "No signature"
    // (userPick = null) sticks rather than getting auto-overwritten on
    // the next render. ``undefined`` = no explicit pick yet.
    const [userPickedSignatureId, setUserPickedSignatureId] = useState<
      number | null | undefined
    >(undefined)
    const selectedSignatureId =
      userPickedSignatureId === undefined
        ? (defaultSignature?.id ?? null)
        : userPickedSignatureId
    const selectedSignature =
      signatures.find((s) => s.id === selectedSignatureId) ?? null
    const hasNoSignatures = !!signaturesPage && signatures.length === 0

    // z-index for the compact signature dropdown's popover so it layers above
    // the surrounding modal/sider chrome (SignatureFooter does the same).
    const compactSigModalDepth = useModalDepth()
    const compactSigZIndex = useMemo(
      () => getDropdownZIndex(compactSigModalDepth),
      [compactSigModalDepth]
    )

    const defaultTo = useMemo(
      () => initialTo ?? getDefaultTo(mode, replyToMessage, integrationEmail),
      [initialTo, mode, replyToMessage, integrationEmail]
    )
    const defaultCc = useMemo(
      () => getDefaultCc(mode, replyToMessage, integrationEmail),
      [mode, replyToMessage, integrationEmail]
    )
    const defaultSubject = useMemo(
      () => initialSubject ?? getDefaultSubject(mode, thread, replyToMessage),
      [initialSubject, mode, thread, replyToMessage]
    )
    const defaultBody = useMemo(
      () => initialBody ?? getDefaultBody(mode, replyToMessage),
      [initialBody, mode, replyToMessage]
    )

    const { control, handleSubmit, setValue, register, getValues } =
      useForm<ComposeFormValues>({
        defaultValues: {
          to: defaultTo,
          cc: defaultCc,
          bcc: [],
          from_id: 0,
          subject: defaultSubject,
          body: defaultBody,
        },
        resolver: zodResolver(composeSchema),
        mode: 'onSubmit',
      })

    // In inline reply/reply_all mode the To field isn't rendered (a compact
    // header is shown instead), so register it manually for validation.
    // Also register cc for reply_all so default Cc values are submitted.
    useEffect(() => {
      if (inline && mode !== 'forward') {
        register('to')
        setValue('to', defaultTo)
      }
      if (inline && mode === 'reply_all' && defaultCc.length > 0) {
        register('cc')
        setValue('cc', defaultCc)
      }
    }, [inline, mode, defaultTo, defaultCc, register, setValue])

    // Select the email integration matching the thread's integration email,
    // falling back to the first available integration
    useEffect(() => {
      if (!emailIntegrationOptions?.length) return
      const match = integrationEmail
        ? emailIntegrationOptions.find(
            (opt) => opt.label.toLowerCase() === integrationEmail.toLowerCase()
          )
        : undefined
      setValue('from_id', match?.value ?? emailIntegrationOptions[0].value)
    }, [emailIntegrationOptions, integrationEmail, setValue])

    // Thread-context contacts surfaced in the autocomplete unconditionally.
    // ``initialContacts`` lets parents inject pre-known recipients so the
    // form's pre-populated ``to`` strings can resolve to chips with names.
    const extraContacts = useMemo<EmailContact[]>(
      () => [
        ...(replyToMessage?.sender ?? []),
        ...(replyToMessage?.to ?? []),
        ...(replyToMessage?.cc ?? []),
        ...(initialContacts ?? []),
      ],
      [replyToMessage, initialContacts]
    )

    const {
      options: recipientOptions,
      isLoading: isLoadingRecipients,
      setSearch: setRecipientSearch,
    } = useRecipientOptions(extraContacts)

    const handleRemoveAttachment = createRemoveHandler((index: number) => {
      setAttachments((prev) => prev.filter((_, i) => i !== index))
    })

    const attachmentListProps = {
      attachments,
      onRemove: handleRemoveAttachment,
      maxSize: MAX_EMAIL_ATTACHMENT_SIZE,
      warningThreshold: EMAIL_ATTACHMENT_WARNING_THRESHOLD,
    }

    /** Shared props for To/Cc/Bcc recipient selects. */
    const recipientSelectProps = useMemo(
      () => ({
        ds2026: true,
        height: '32px',
        placeholder: 'Search contacts or team members...',
        isMulti: true as const,
        options: recipientOptions,
        onSearchChange: setRecipientSearch,
        isLoading: isLoadingRecipients,
        className: 'mt-0',
        onCreateOption: createEmailOption,
        isValidNewOption: looksLikeEmail,
        styles: emailRecipientStyles,
        formatOptionLabel: formatEmailRecipientOption,
      }),
      [recipientOptions, isLoadingRecipients, setRecipientSearch]
    )

    /** Resolve a display name from recipientOptions for a given email. */
    const resolveContactName = (email: string): string => {
      const opt = recipientOptions.find((o) => o.value === email)
      if (!opt?.label?.includes('<')) return ''
      return opt.label.split('<')[0].trim()
    }

    /** Map an array of email strings to {name, email} contact objects. */
    const buildRecipients = (emails: string[]) =>
      emails.map((email) => ({ name: resolveContactName(email), email }))

    const onSubmit = handleSubmit((values) => {
      // Prevent sending with uninitialized from_id when this panel owns
      // the send. In ``embedded`` mode the parent (the rec apply
      // endpoint) resolves the integration server-side from the rec
      // owner — the FE-selected from_id is never read, so blocking on
      // it here would silently swallow the submit if the user clicked
      // Apply before the integration-options query resolved.
      if (!embedded && !values.from_id) return

      const toRecipients = buildRecipients(values.to)
      const ccRecipients = values.cc?.length ? buildRecipients(values.cc) : null
      const bccRecipients = values.bcc?.length
        ? buildRecipients(values.bcc)
        : null

      // Append the selected signature (if any) below the user's body.
      // The two-line gap mirrors the spacing email clients add between
      // body and signature. We intentionally send `selectedSignature.html`
      // raw rather than the `sanitizedHtml` used for preview — DOMPurify's
      // defaults strip inline styles, color attributes, and some CSS that
      // signatures legitimately need. The signature HTML is sanitized
      // server-side at create-time so what's stored is already safe.
      const bodyWithSignature = selectedSignature
        ? `${values.body}<br><br>${selectedSignature.html}`
        : values.body

      // Build FormData for the API call
      const formData = new FormData()
      formData.append('to', JSON.stringify(toRecipients))
      formData.append('subject', values.subject)
      formData.append('body', bodyWithSignature)

      if (
        (mode === 'reply' || mode === 'reply_all') &&
        replyToMessage?.email_id
      ) {
        formData.append('reply_to_message_id', replyToMessage.email_id)
      }

      if (ccRecipients) formData.append('cc', JSON.stringify(ccRecipients))
      if (bccRecipients) formData.append('bcc', JSON.stringify(bccRecipients))

      attachments.forEach((attachment) => {
        if (isNewAttachment(attachment)) {
          formData.append('attachments', attachment.file)
        }
      })

      // Build optimistic message from form values
      const optimisticMessage: EmailMessage = {
        id: -Date.now(),
        outbound: true,
        related_contacts: [],
        sender: [
          {
            name: `${user?.first_name ?? ''} ${user?.last_name ?? ''}`.trim(),
            email: integrationEmail || '',
          },
        ],
        to: toRecipients,
        cc: ccRecipients,
        subject: values.subject,
        snippet: bodyWithSignature.replace(/<[^>]*>/g, '').slice(0, 100),
        date: new Date().toISOString(),
        body: bodyWithSignature,
        attachments: [],
      }

      onSend({ optimisticMessage, formData, fromId: values.from_id })
    })

    // Expose imperative submit for parents in embedded mode (the toolbar's
    // own Send button is hidden, so the parent triggers send externally).
    useImperativeHandle(ref, () => ({ submit: () => onSubmit() }), [onSubmit])

    /** Summarize who the reply is addressed to for the compact inline header. */
    const inlineRecipientLabel = useMemo(() => {
      if (!defaultTo.length) return ''

      const resolveLabel = (email: string) => {
        const opt = recipientOptions.find((o) => o.value === email)
        if (!opt?.label) return email
        // Show name only (strip "<email>" suffix) like Gmail
        const name = opt.label.split('<')[0].trim()
        return name || email
      }

      // Show up to 2 names before collapsing to +N
      if (defaultTo.length <= 2) {
        return defaultTo.map(resolveLabel).join(', ')
      }
      const firstLabel = resolveLabel(defaultTo[0])
      return `${firstLabel} (+${defaultTo.length - 1})`
    }, [defaultTo, recipientOptions])

    const userInitials =
      [user?.first_name, user?.last_name]
        .filter(Boolean)
        .map((n) => n![0].toUpperCase())
        .join('')
        .slice(0, 2) || 'UN'

    const sendDisabled = !emailIntegrationOptions?.length
    const sendButton = (
      <Ds2026ButtonProvider>
        <FbButton
          type="button"
          variant="primary"
          size="sm"
          disabled={sendDisabled}
          onClick={() => onSubmit()}
          className="h-8 !min-h-0 px-3.5 whitespace-nowrap"
        >
          Send
        </FbButton>
      </Ds2026ButtonProvider>
    )

    const toolbarRight = embedded ? null : (
      <div className="flex items-center gap-2.5">
        {dealIdentifier && (
          <DealBadge dealIdentifier={dealIdentifier} dealName={dealName} />
        )}
        {!inline && companyName && (
          <span className={COMPANY_BADGE_CLASS}>{companyName}</span>
        )}
        <Ds2026ButtonProvider>
          <FbButton
            type="button"
            variant="secondary"
            size="sm"
            onClick={onDiscard}
            className="h-8 !min-h-0 px-3.5 whitespace-nowrap"
          >
            Cancel
          </FbButton>
        </Ds2026ButtonProvider>
        {sendButton}
      </div>
    )

    // Compact-mode signature picker: replaces the full SignatureFooter
    // (~80px tall row + HTML preview) with a single inline chip in the
    // toolbar. Drops the preview entirely so the body editor reclaims
    // the vertical space. Used in height-constrained embeds like the
    // rec sider's apply panel.
    const compactSignaturePicker = compactSignature ? (
      hasNoSignatures ? (
        <Link
          to="/settings/email-integration/signatures"
          className="inline-flex items-center gap-1 text-xs font-medium text-yellow-700 hover:text-yellow-900 whitespace-nowrap"
        >
          <PiWarning size={12} />
          Add signature
        </Link>
      ) : (
        <div className="flex items-center gap-1.5 whitespace-nowrap">
          <span className="text-sm text-neutral-500">Signature:</span>
          {/* DS2026 button + popover (mirrors the full SignatureFooter picker),
              sized down for the compact toolbar — replaces the native select. */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                aria-label="Email signature"
                className="inline-flex items-center gap-1 h-7 max-w-[140px] rounded-md border border-neutral-300 bg-white px-2 text-sm font-medium text-neutral-800 transition-colors hover:bg-neutral-100 data-[state=open]:bg-neutral-100"
              >
                <span className="truncate">
                  {selectedSignature
                    ? `${selectedSignature.name}${selectedSignature.is_default ? ' (default)' : ''}`
                    : 'None'}
                </span>
                <PiCaretDown className="size-3 flex-shrink-0 text-neutral-500" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="start"
              style={{ minWidth: 140, zIndex: compactSigZIndex }}
              className={ds2026DropdownContentClass}
            >
              <DropdownMenuItem
                className={cn(ds2026DropdownItemClass, '!text-sm')}
                onSelect={() => setUserPickedSignatureId(null)}
              >
                <span className="flex-1">None</span>
                {selectedSignatureId == null && (
                  <PiCheck className="size-3.5 flex-shrink-0 text-brand-active" />
                )}
              </DropdownMenuItem>
              {signatures.map((s) => (
                <DropdownMenuItem
                  key={s.id}
                  className={cn(ds2026DropdownItemClass, '!text-sm')}
                  onSelect={() => setUserPickedSignatureId(s.id)}
                >
                  <span className="flex-1 truncate">
                    {s.name}
                    {s.is_default ? ' (default)' : ''}
                  </span>
                  {selectedSignatureId === s.id && (
                    <PiCheck className="size-3.5 flex-shrink-0 text-brand-active" />
                  )}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          {/* Gear → signature config. Visible affordance for "edit my
              signatures" without a separate Configure link taking up
              horizontal space. */}
          <Link
            to="/settings/email-integration/signatures"
            aria-label="Configure signatures"
            title="Configure signatures"
            className="text-brand-active hover:text-brand-hover transition-colors flex-shrink-0"
          >
            <PiGear size={16} />
          </Link>
        </div>
      )
    ) : null

    // Null when both toggles are already revealed so `RecipientRow` doesn't
    // render an empty trailing-slot wrapper that still eats `gap-2`.
    const ccBccToggles =
      showCc && showBcc ? null : (
        <CcBccToggles
          showCc={showCc}
          showBcc={showBcc}
          onShowCc={() => setShowCc(true)}
          onShowBcc={() => setShowBcc(true)}
        />
      )

    /** Collapse empty CC/BCC fields when the Quill editor content area gains focus. */
    const collapseEmptyRecipientFields = (e: React.FocusEvent) => {
      // Only collapse when the actual editor content (.ql-editor) is focused,
      // not toolbar buttons (Send, Cancel, attachment, formatting).
      const target = e.target as HTMLElement
      if (!target.closest('.ql-editor')) return
      const values = getValues()
      if (showCc && (!values.cc || values.cc.length === 0)) setShowCc(false)
      if (showBcc && (!values.bcc || values.bcc.length === 0)) setShowBcc(false)
    }

    // Replaces the form when there are no sendable accounts. `flex-1` +
    // `min-h` so it centers vertically instead of hugging the top.
    const noIntegrationsState = (
      <div className="flex flex-1 flex-col items-center justify-center gap-3 px-4 py-10 text-center min-h-[18rem]">
        <PiEnvelopeSimple size={32} className="text-neutral-400" />
        <div className="space-y-1">
          <p className="text-sm font-medium text-neutral-800">
            No connected email account
          </p>
          <p className="text-xs text-neutral-500 max-w-[28rem]">
            You need at least one connected, non-expired email account to send
            messages from First Bite.
          </p>
        </div>
        <Link
          to="/settings/email-integration/connections"
          className="text-sm font-medium text-brand-active hover:text-brand-pressed"
        >
          Go to email integrations
        </Link>
      </div>
    )

    // Hidden in inline (thread implies the account) and embedded (rec card
    // owns send and ignores `from_id`).
    const showFromRow =
      !inline && !embedded && (emailIntegrationOptions?.length ?? 0) > 0
    const fromRow = showFromRow ? (
      <RecipientRow label="From">
        <RecipientSelect
          ds2026
          height="32px"
          control={control}
          name="from_id"
          options={emailIntegrationOptions}
          isMulti={false}
          isClearable={false}
          disableCreate
          onCreateOption={async () => null}
          placeholder=""
          styles={emailRecipientStyles}
          formatOptionLabel={formatIntegrationOption}
          className="mt-0"
        />
      </RecipientRow>
    ) : null

    /** Shared compose fields (To/Cc/Bcc/Subject/Body/Attachments). */
    const composeFields = (
      <>
        {/* Compact inline header for reply/reply_all (not forward) */}
        {inline && mode !== 'forward' && (
          <InlineReplyHeader
            mode={mode as ReplyMode}
            label={inlineRecipientLabel}
            onModeChange={onModeChange as ((m: ReplyMode) => void) | undefined}
            trailing={ccBccToggles}
          />
        )}

        <div
          className={cn(
            inline ? 'px-3 pt-3 flex-1 min-h-0 overflow-y-auto' : '',
            standalone && 'flex-1 min-h-0 flex flex-col gap-1.5'
          )}
        >
          {fromRow}
          {/* To field for forward and standalone compose */}
          {(mode === 'forward' || !inline) &&
            (standalone || inline ? (
              <RecipientRow label="To" trailing={ccBccToggles}>
                <RecipientSelect
                  label={undefined}
                  control={control}
                  name="to"
                  {...recipientSelectProps}
                  placeholder=""
                />
              </RecipientRow>
            ) : (
              <>
                <RecipientSelect
                  label="To"
                  control={control}
                  name="to"
                  {...recipientSelectProps}
                />
                {ccBccToggles}
              </>
            ))}

          {/* Cc field */}
          {showCc &&
            (standalone || inline ? (
              <RecipientRow
                label="Cc"
                onRemove={() => {
                  setShowCc(false)
                  setValue('cc', [])
                }}
              >
                <RecipientSelect
                  label={undefined}
                  control={control}
                  name="cc"
                  {...recipientSelectProps}
                  placeholder=""
                />
              </RecipientRow>
            ) : (
              <RecipientSelect
                label="Cc"
                control={control}
                name="cc"
                {...recipientSelectProps}
              />
            ))}

          {/* Bcc field */}
          {showBcc &&
            (standalone || inline ? (
              <RecipientRow
                label="Bcc"
                onRemove={() => {
                  setShowBcc(false)
                  setValue('bcc', [])
                }}
              >
                <RecipientSelect
                  label={undefined}
                  control={control}
                  name="bcc"
                  {...recipientSelectProps}
                  placeholder=""
                />
              </RecipientRow>
            ) : (
              <RecipientSelect
                label="Bcc"
                control={control}
                name="bcc"
                {...recipientSelectProps}
              />
            ))}

          {/* Subject — only for standalone compose and forward.
              ``subjectPrefix`` (when supplied) renders to the left of the
              input on the same row so callers can attach contextual
              chrome (e.g. "See thread" link, "New thread" badge) to the
              subject line without touching the subject's value. */}
          {!inline &&
            (standalone ? (
              <div className="py-0.5 px-2 cursor-text [&_.border-input]:!border-0 [&_input]:!bg-transparent [&_input]:!h-8 flex items-center gap-2">
                <div className="text-sm text-neutral-500 w-12 flex-shrink-0 font-medium text-left">
                  Subject
                </div>
                {subjectPrefix && (
                  <div className="flex-shrink-0">{subjectPrefix}</div>
                )}
                <div className="flex-1 min-w-0">
                  <TextInput
                    control={control}
                    name="subject"
                    type="text"
                    label={undefined}
                    placeholder=""
                    noMargin
                    autoComplete="off"
                    className="[&_input]:!text-sm"
                  />
                </div>
                {subjectSuffix && (
                  <div className="flex-shrink-0">{subjectSuffix}</div>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-2">
                {subjectPrefix && (
                  <div className="flex-shrink-0">{subjectPrefix}</div>
                )}
                <div className="flex-1 min-w-0">
                  <TextInput
                    control={control}
                    name="subject"
                    type="text"
                    label="Subject"
                    placeholder="Subject"
                    noMargin
                    className="[&_label]:text-neutral-400 [&_input]:placeholder:text-neutral-500"
                  />
                </div>
                {subjectSuffix && (
                  <div className="flex-shrink-0">{subjectSuffix}</div>
                )}
              </div>
            ))}

          {/* Body editor */}
          <div
            onFocusCapture={collapseEmptyRecipientFields}
            className={cn(
              inline ? 'mt-0' : 'mt-2',
              standalone &&
                'email-compose-body flex-1 min-h-0 flex flex-col overflow-hidden rounded-xl border border-neutral-200 [&>div]:flex-1 [&>div]:min-h-0 [&>div]:flex [&>div]:flex-col [&>div>div]:flex-1 [&>div>div]:min-h-0 [&>div>div]:flex [&>div>div]:flex-col'
            )}
          >
            <StyledEditorRHF
              name="body"
              noMargin
              // Focus the body on reply/reply-all/forward so the user can type
              // immediately (FIR-7701); new compose keeps focus on the To field.
              autoFocus={inline}
              borderless={inline || standalone}
              className={cn(
                !standalone &&
                  'min-h-28 [&_.ql-container]:min-h-28 [&_.ql-editor]:min-h-28',
                inline && '[&_.ql-editor]:!py-2 [&_.ql-editor]:!px-2',
                standalone && [
                  'flex-1 flex flex-col min-h-0',
                  '[&_.ql-container]:flex-1 [&_.ql-container]:flex [&_.ql-container]:flex-col [&_.ql-container]:min-h-0 [&_.ql-container]:!border-0',
                  '[&_.ql-editor]:flex-1 [&_.ql-editor]:min-h-0 [&_.ql-editor]:!overflow-y-auto',
                  // Inner padding + 14px text/placeholder live in Editor.css under
                  // `.email-compose-body` (set on the wrapper div above, which
                  // Quill never overwrites — unlike this editor container's own
                  // className, which Quill resets at mount).
                  '[&_.ql-toolbar]:!border-0',
                ]
              )}
              control={control}
              editorPadding={standalone ? '12px 16px' : undefined}
              placeholder={inline ? '' : 'Write your message...'}
              toolbarPrefix={!embedded && compactSignature ? sendButton : null}
              toolbarSuffix={
                compactSignature ? compactSignaturePicker : toolbarRight
              }
              toolbarClassName={
                inline ? 'sticky bottom-0 bg-surface z-10' : undefined
              }
              aboveToolbar={
                compactSignature ? null : (
                  <SignatureFooter
                    signatures={signatures}
                    selectedSignature={selectedSignature}
                    selectedSignatureId={selectedSignatureId}
                    onChange={setUserPickedSignatureId}
                    hasNoSignatures={hasNoSignatures}
                  />
                )
              }
              additionalToolbarButtons={[
                // Rendered as a button so it picks up the same gray hover as the
                // formatting buttons (.email-toolbar .ql-toolbar button:hover).
                <button
                  key="attachments"
                  type="button"
                  onClick={(e) => {
                    // Drop focus so the button doesn't stay in its hover/focus
                    // state after the native file dialog closes.
                    e.currentTarget.blur()
                    openFileSelect()
                  }}
                  className="flex items-center justify-center size-7 rounded-md text-neutral-600 transition-colors hover:bg-neutral-100 hover:text-neutral-900 focus:outline-none [&_svg]:!h-[1.05rem] [&_svg]:!w-[1.05rem]"
                >
                  <PiPaperclip />
                </button>,
              ]}
            />
          </div>

          {/* Attachments — inside scroll area for standalone, outside for inline */}
          {!inline && <AttachmentList {...attachmentListProps} />}
        </div>
        {inline && attachments.length > 0 && (
          <div className="px-3 pb-2 flex-shrink-0">
            <AttachmentList {...attachmentListProps} />
          </div>
        )}
      </>
    )

    // Embedded (rec card) is exempt -- its host has its own gate.
    const renderedBody =
      !embedded && hasNoSendableIntegrations
        ? noIntegrationsState
        : composeFields

    return (
      <>
        {inline ? (
          <InlineComposeShell userInitials={userInitials}>
            {renderedBody}
          </InlineComposeShell>
        ) : (
          <div
            className={cn(
              standalone && !compactSignature
                ? 'flex flex-col flex-1 min-h-0'
                : standalone && compactSignature
                  ? // Compact mode (rec sider embed): only trim pt-7 → pt-2.
                    // The original pt-7 existed to breathe under the inbox
                    // modal's close-X, which isn't present here. Bottom
                    // padding stays at pb-4 — dropping it makes the
                    // toolbar look flush against the panel edge.
                    'flex flex-col flex-1 min-h-0 px-4 pt-2 pb-4'
                  : // Embedded path (mode="compose", no standalone prop): currently has no
                    // callers — both active callers pass standalone. Parent is expected
                    // to provide container chrome (border/shadow/padding) if this is ever used.
                    'p-4 mt-3 bg-surface'
            )}
          >
            {renderedBody}
          </div>
        )}

        <input
          type="file"
          hidden
          multiple
          ref={fileInputRef}
          onChange={(e) => handleFileSelect(e, attachments)}
          accept={getAcceptString(ALLOWED_EMAIL_ATTACHMENT_TYPES)}
        />
      </>
    )
  }
)
